import {
  addCalendarDays,
  calendarDateFromParts,
  isCalendarDate,
  partsOf,
  startOfWeek,
  weekdayOf,
  type CalendarDate,
} from './dates'

/**
 * Natural-language date parsing for quick capture.
 *
 * "call dentist tomorrow" should become a task called "call dentist" dated
 * tomorrow, without anyone opening a date picker. Capture has to take seconds
 * or it does not happen.
 *
 * Hand-rolled rather than pulling in a general NLP date library: the set of
 * phrases one person actually types is small, the semantics of "next Monday"
 * are genuinely ambiguous and worth pinning down explicitly, and this way the
 * behaviour is covered by tests we own.
 *
 * Semantics, chosen to be predictable rather than clever:
 *
 *   monday          The next Monday, counting today if today is Monday.
 *   next monday     The Monday of next week, always.
 *   next week       The start of next week.
 *
 * Only one date is taken from a string, the first one found. Nothing here
 * guesses at times of day — the app has no concept of them yet.
 */

export type Capture = {
  title: string
  date?: CalendarDate
}

export type CaptureContext = {
  today: CalendarDate
  weekStartsOn: number
}

const WEEKDAYS: ReadonlyArray<readonly [string, number]> = [
  ['sunday', 0],
  ['sun', 0],
  ['monday', 1],
  ['mon', 1],
  ['tuesday', 2],
  ['tues', 2],
  ['tue', 2],
  ['wednesday', 3],
  ['weds', 3],
  ['wed', 3],
  ['thursday', 4],
  ['thurs', 4],
  ['thu', 4],
  ['friday', 5],
  ['fri', 5],
  ['saturday', 6],
  ['sat', 6],
]

const MONTHS: ReadonlyArray<readonly [string, number]> = [
  ['january', 1],
  ['jan', 1],
  ['february', 2],
  ['feb', 2],
  ['march', 3],
  ['mar', 3],
  ['april', 4],
  ['apr', 4],
  ['may', 5],
  ['june', 6],
  ['jun', 6],
  ['july', 7],
  ['jul', 7],
  ['august', 8],
  ['aug', 8],
  ['september', 9],
  ['sept', 9],
  ['sep', 9],
  ['october', 10],
  ['oct', 10],
  ['november', 11],
  ['nov', 11],
  ['december', 12],
  ['dec', 12],
]

const WEEKDAY_ALTERNATION = WEEKDAYS.map(([name]) => name).join('|')
const MONTH_ALTERNATION = MONTHS.map(([name]) => name).join('|')

function lookup(
  table: ReadonlyArray<readonly [string, number]>,
  name: string,
): number | null {
  const match = table.find(([key]) => key === name)
  return match === undefined ? null : match[1]
}

/** The next date with this weekday, today included when it matches. */
function nextWeekday(from: CalendarDate, weekday: number): CalendarDate {
  const offset = (weekday - weekdayOf(from) + 7) % 7
  return addCalendarDays(from, offset)
}

/** The same weekday, in the week after the one containing `from`. */
function weekdayNextWeek(
  from: CalendarDate,
  weekday: number,
  weekStartsOn: number,
): CalendarDate {
  const nextWeekStart = addCalendarDays(startOfWeek(from, weekStartsOn), 7)
  const offset = (weekday - weekdayOf(nextWeekStart) + 7) % 7
  return addCalendarDays(nextWeekStart, offset)
}

/**
 * The month/day pair as a future date: this year if it is still ahead,
 * otherwise next year. Typing "15 jan" in December means the coming January.
 */
function upcomingMonthDay(
  today: CalendarDate,
  month: number,
  day: number,
): CalendarDate {
  const { year } = partsOf(today)
  const thisYear = calendarDateFromParts(year, month, day)
  return thisYear >= today
    ? thisYear
    : calendarDateFromParts(year + 1, month, day)
}

type Rule = {
  pattern: RegExp
  resolve: (
    match: RegExpMatchArray,
    context: CaptureContext,
  ) => CalendarDate | null
}

/**
 * Ordered: the first match wins, so more specific phrases must come before the
 * shorter ones they contain. "next monday" has to beat a bare "monday", and
 * "in 2 weeks" has to beat "in 2 days" losing its unit.
 */
const RULES: Rule[] = [
  {
    pattern: /\b(\d{4}-\d{2}-\d{2})\b/,
    resolve: (match) => {
      const value = match[1]
      if (value === undefined || !isCalendarDate(value)) return null
      return value
    },
  },
  {
    pattern: /\b(today|tonight)\b/,
    resolve: (_match, { today }) => today,
  },
  {
    pattern: /\b(tomorrow|tmr|tmrw)\b/,
    resolve: (_match, { today }) => addCalendarDays(today, 1),
  },
  {
    pattern: new RegExp(`\\bnext\\s+(${WEEKDAY_ALTERNATION})\\b`),
    resolve: (match, { today, weekStartsOn }) => {
      const weekday = lookup(WEEKDAYS, match[1] ?? '')
      if (weekday === null) return null
      return weekdayNextWeek(today, weekday, weekStartsOn)
    },
  },
  {
    pattern: /\bnext\s+week\b/,
    resolve: (_match, { today, weekStartsOn }) =>
      addCalendarDays(startOfWeek(today, weekStartsOn), 7),
  },
  {
    pattern: /\bin\s+(\d+)\s+weeks?\b/,
    resolve: (match, { today }) => {
      const weeks = Number(match[1])
      if (!Number.isFinite(weeks)) return null
      return addCalendarDays(today, weeks * 7)
    },
  },
  {
    pattern: /\bin\s+(\d+)\s+days?\b/,
    resolve: (match, { today }) => {
      const days = Number(match[1])
      if (!Number.isFinite(days)) return null
      return addCalendarDays(today, days)
    },
  },
  {
    pattern: /\bin\s+a\s+week\b/,
    resolve: (_match, { today }) => addCalendarDays(today, 7),
  },
  {
    // "15 aug", "15th august"
    pattern: new RegExp(
      `\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_ALTERNATION})\\b`,
    ),
    resolve: (match, { today }) => {
      const day = Number(match[1])
      const month = lookup(MONTHS, match[2] ?? '')
      if (month === null || day < 1 || day > 31) return null
      return upcomingMonthDay(today, month, day)
    },
  },
  {
    // "aug 15", "august 15th"
    pattern: new RegExp(
      `\\b(${MONTH_ALTERNATION})\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`,
    ),
    resolve: (match, { today }) => {
      const month = lookup(MONTHS, match[1] ?? '')
      const day = Number(match[2])
      if (month === null || day < 1 || day > 31) return null
      return upcomingMonthDay(today, month, day)
    },
  },
  {
    pattern: new RegExp(`\\b(?:on\\s+)?(${WEEKDAY_ALTERNATION})\\b`),
    resolve: (match, { today }) => {
      const weekday = lookup(WEEKDAYS, match[1] ?? '')
      if (weekday === null) return null
      return nextWeekday(today, weekday)
    },
  },
]

/** Collapse the whitespace left behind by removing a phrase mid-sentence. */
function tidy(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .trim()
}

export function parseCapture(text: string, context: CaptureContext): Capture {
  const original = text.trim()
  const haystack = original.toLowerCase()

  for (const rule of RULES) {
    const match = haystack.match(rule.pattern)
    if (match === null || match.index === undefined) continue

    const date = rule.resolve(match, context)
    if (date === null) continue

    /**
     * "every monday" is a recurrence, not a date.
     *
     * Consuming the weekday turned it into a one-off dated today-ish and left
     * the orphaned word "every" sitting in the title — a wrong date and a
     * mangled name, from a phrase the placeholder invites. Recurrence is not
     * something capture can express, so the honest move is to decline the
     * whole phrase rather than take the half it recognises.
     */
    const before = original.slice(0, match.index).trimEnd().toLowerCase()
    if (/\bevery$/.test(before)) continue

    const stripped = tidy(
      original.slice(0, match.index) +
        ' ' +
        original.slice(match.index + match[0].length),
    )

    // "tomorrow" on its own is a date with no task attached. Keeping the
    // original text beats creating an item with a blank title.
    if (stripped.length === 0) return { title: original, date }

    return { title: stripped, date }
  }

  return { title: original }
}
